// OWNER: FOUNDATION. Component gallery for visual checks; not linked from the nav.
// Open http://localhost:3000/kit to see every shared component in the current theme.
import { createFileRoute } from '@tanstack/react-router'
import { Inbox, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { MESSAGE_STATUSES } from '@/api/types'
import { CodeBlock } from '@/components/CodeBlock'
import { CodeEditor, type ContextTree } from '@/components/code-editor'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { CopyButton, IdText } from '@/components/CopyButton'
import { DiffView } from '@/components/DiffView'
import { EmailPreviewFrame } from '@/components/EmailPreviewFrame'
import { EmptyState } from '@/components/EmptyState'
import { JsonViewer } from '@/components/JsonViewer'
import { KeyValueList } from '@/components/KeyValueList'
import { PageHeader } from '@/components/PageHeader'
import { RelativeTime } from '@/components/RelativeTime'
import { SecretReveal } from '@/components/SecretReveal'
import { StatusBadge } from '@/components/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'
import { Input, Textarea } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { Select } from '@/components/ui/select'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export const Route = createFileRoute('/kit')({
  staticData: { crumb: 'Component kit' },
  component: KitPage,
})

const sample = {
  order: { number: '1042', total: 129.5, currency: 'USD', gift: false },
  customer: { email: 'ann@example.com', first_name: 'Ann' },
  items: [
    { sku: 'MUG-01', name: 'Enamel mug', qty: 2 },
    { sku: 'TEE-07', name: 'Forge tee', qty: 1 },
  ],
}

const ctx: ContextTree = {
  template: [
    {
      name: 'payload',
      kind: 'object',
      description: 'The event payload',
      children: [
        {
          name: 'order',
          kind: 'object',
          children: [
            { name: 'number', kind: 'string', example: '"1042"', children: [] },
            { name: 'total', kind: 'number', example: '129.5', children: [] },
          ],
        },
        {
          name: 'customer',
          kind: 'object',
          children: [
            { name: 'email', kind: 'string', example: '"ann@example.com"', children: [] },
            {
              name: 'first_name',
              kind: 'string',
              description: 'Given name',
              example: '"Ann"',
              children: [],
            },
          ],
        },
        {
          name: 'items',
          kind: 'array',
          children: [
            { name: 'sku', kind: 'string', children: [] },
            { name: 'name', kind: 'string', children: [] },
            { name: 'qty', kind: 'integer', children: [] },
          ],
        },
      ],
    },
    {
      name: 'app',
      kind: 'object',
      children: [
        { name: 'name', kind: 'string', children: [] },
        {
          name: 'vars',
          kind: 'object',
          children: [{ name: 'brandName', kind: 'string', example: '"Acme"', children: [] }],
        },
      ],
    },
    { name: 'vars', kind: 'object', children: [] },
    { name: 'now', kind: 'string', children: [] },
  ],
  expression: [],
  helpers: [
    {
      name: 'formatCurrency',
      signature: 'formatCurrency n "USD"',
      description: 'Format a number as money.',
      example: '{{formatCurrency payload.order.total "USD"}}',
    },
    {
      name: 'eq',
      signature: 'eq a b',
      description: 'True when a equals b.',
      example: '{{#if (eq a b)}}…{{/if}}',
    },
  ],
  cel_functions: [
    {
      name: 'lower',
      signature: 'lower(string) -> string',
      description: 'Lowercase.',
      example: 'lower(payload.customer.email)',
    },
  ],
}
ctx.expression = ctx.template.filter((s) => s.name !== 'vars')

const anHourAgo = new Date(Date.now() - 3_600_000).toISOString()

const html = `<!doctype html><html><head><style>body{font-family:Georgia,serif;padding:24px;color:#222}h1{font-size:22px}</style></head>
<body><h1>Your order shipped</h1><p>Hi Ann, order <b>#1042</b> is on its way.</p><a href="https://example.com">Track it</a></body></html>`

function KitPage() {
  const [body, setBody] = useState(
    '<p>Hi {{payload.customer.first_name}},</p>\n{{#each payload.items}}\n  <li>{{name}} × {{qty}}</li>\n{{/each}}\n{{> footer}}',
  )
  const [subject, setSubject] = useState('Order {{payload.order.number}} shipped')
  const [cel, setCel] = useState(
    'has(payload.customer.email) ? payload.customer.email : app.vars.brandName',
  )
  const [json, setJson] = useState(JSON.stringify(sample, null, 2))
  const [sel, setSel] = useState<'off' | 'warn' | 'enforce'>('warn')
  const [combo, setCombo] = useState<string | null>('acme')
  const [confirm, setConfirm] = useState(false)
  const [sheet, setSheet] = useState(false)
  return (
    <>
      <PageHeader
        title="Component kit"
        description="Every shared component in the current theme. Use it to check visual changes."
        meta={
          <>
            <Badge tone="brass">Foundation</Badge>
            <IdText id="msg_01JA6Z8F3K2M9QYV4T7W1XBCDE" />
          </>
        }
        actions={
          <>
            <Button icon={<Trash2 />} variant="danger-ghost" onClick={() => setConfirm(true)}>
              Delete
            </Button>
            <Button variant="primary" icon={<Plus />} onClick={() => setSheet(true)}>
              Open sheet
            </Button>
          </>
        }
      />
      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <CardHeader title="Buttons and inputs" />
          <CardBody className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="primary">Primary</Button>
              <Button>Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button loading>Saving</Button>
              <Button variant="link">Link</Button>
              <IconButton label="Add">
                <Plus />
              </IconButton>
              <Kbd>⌘K</Kbd>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="Text input" />
              <Input placeholder="Invalid" aria-invalid />
              <Select
                aria-label="Validation"
                value={sel}
                onValueChange={setSel}
                options={[
                  { value: 'off', label: 'Off' },
                  { value: 'warn', label: 'Warn', description: 'Log a warning' },
                  { value: 'enforce', label: 'Enforce' },
                ]}
              />
              <Combobox
                aria-label="Application"
                value={combo}
                onValueChange={setCombo}
                allowClear
                options={[
                  { value: 'acme', label: 'Acme Shop', hint: 'acme-shop' },
                  { value: 'id', label: 'Identity', hint: 'identity' },
                ]}
              />
            </div>
            <Textarea placeholder="Textarea" />
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <Checkbox defaultChecked /> Checkbox
              </label>
              <label className="flex items-center gap-2">
                <Switch defaultChecked /> Switch
              </label>
              <CopyButton value="copied">Copy</CopyButton>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Status and data" />
          <CardBody className="space-y-4">
            <div className="flex flex-wrap gap-1.5">
              {MESSAGE_STATUSES.map((s) => (
                <StatusBadge key={s} status={s} />
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(
                ['neutral', 'brass', 'success', 'warning', 'danger', 'info', 'outline'] as const
              ).map((t) => (
                <Badge key={t} tone={t}>
                  {t}
                </Badge>
              ))}
            </div>
            <KeyValueList
              items={[
                {
                  label: 'Created',
                  value: <RelativeTime value={anHourAgo} />,
                },
                { label: 'Owner', value: 'Checkout' },
                { label: 'Empty', value: '' },
              ]}
            />
            <SecretReveal secret="ms_test_exampl3k_thisIsNotARealKeyJustForTheKitDemo" />
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="CodeEditor"
            description="Type {{ or . for completions; CEL suggests payload, app, now."
          />
          <CardBody className="space-y-2">
            <CodeEditor
              language="handlebars"
              singleLine
              height="auto"
              value={subject}
              onChange={setSubject}
              path="kit:subject"
              completionContext={ctx}
              aria-label="Subject"
            />
            <CodeEditor
              language="handlebars-html"
              height={180}
              value={body}
              onChange={setBody}
              path="kit:body"
              completionContext={ctx}
              partials={['footer', 'layout', 'button']}
              aria-label="Body"
            />
            <CodeEditor
              language="cel"
              singleLine
              height="auto"
              value={cel}
              onChange={setCel}
              path="kit:cel"
              completionContext={ctx}
              aria-label="CEL"
            />
            <CodeEditor
              language="json"
              height={140}
              value={json}
              onChange={setJson}
              path="kit:json"
              jsonSchema={{
                type: 'object',
                required: ['order'],
                properties: { order: { type: 'object' } },
              }}
              aria-label="JSON"
            />
          </CardBody>
        </Card>
        <EmailPreviewFrame
          html={html}
          subject="Your order shipped"
          from="Acme <orders@acme.test>"
          to="ann@example.com"
          height={360}
        />
        <Card>
          <CardHeader title="JSON" />
          <CardBody>
            <Tabs defaultValue="tree">
              <TabsList>
                <TabsTrigger value="tree">Tree</TabsTrigger>
                <TabsTrigger value="raw">Raw</TabsTrigger>
              </TabsList>
              <TabsContent value="tree">
                <JsonViewer value={sample} />
              </TabsContent>
              <TabsContent value="raw">
                <CodeBlock value={sample} />
              </TabsContent>
            </Tabs>
          </CardBody>
        </Card>
        <DiffView
          oldText={'<h1>Shipped</h1>\n<p>Hi {{name}}</p>\n<p>Thanks</p>'}
          newText={
            '<h1>Your order shipped</h1>\n<p>Hi {{name}}</p>\n<p>Thanks for shopping</p>\n{{> footer}}'
          }
          oldLabel="v2"
          newLabel="Draft"
        />
        <Card className="xl:col-span-2">
          <EmptyState
            icon={Inbox}
            title="No messages yet"
            description="Messages appear here once an application sends its first event."
            action={<Button variant="primary">Send a test event</Button>}
          />
        </Card>
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Delete template?"
        description="This removes every version. Messages already sent keep their content."
        confirmLabel="Delete template"
        destructive
        confirmText="order-shipped"
        onConfirm={() => new Promise((r) => setTimeout(r, 600))}
      />
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent
          title="Sheet"
          description="Side panel for secondary forms."
          footer={
            <Button variant="primary" onClick={() => setSheet(false)}>
              Done
            </Button>
          }
        >
          <JsonViewer value={sample} />
        </SheetContent>
      </Sheet>
    </>
  )
}
